import React, { useState, useEffect } from 'react';
import { usersApi, unitsApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { 
  Users, 
  Trash2,
  Shield,
  UserCheck,
  Eye,
  Plus,
  UserPlus,
  Truck,
  Pencil
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';

export default function UserManagement({ embedded = false }) {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'dispatcher',
    assigned_unit_id: '',
    permissions: {
      can_view_weekly_summary: true,
      can_view_driver_files: true,
      can_upload_files: true,
    },
    feature_access: {
      schedule: true, accounting: true, deductions: true,
      recurring_deductions: true, history: true, owner_operators: true,
      vehicle_management: true, driver_files: true,
      limit_to_assigned_units: false,
    },
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usersRes, unitsRes] = await Promise.all([
        usersApi.getAll(),
        unitsApi.getAll()
      ]);
      setUsers(usersRes.data);
      setUnits(unitsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        role: formData.role,
        feature_access: formData.role !== 'driver' ? formData.feature_access : undefined,
      };
      
      // Add driver-specific fields
      if (formData.role === 'driver') {
        if (!formData.assigned_unit_id) {
          toast.error('Please select a unit for the driver');
          return;
        }
        payload.assigned_unit_id = formData.assigned_unit_id;
        payload.permissions = formData.permissions;
      }
      
      await usersApi.create(payload);
      toast.success('User created successfully');
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name,
        role: formData.role,
        feature_access: formData.role !== 'driver' ? formData.feature_access : undefined,
      };
      
      // Add driver-specific fields
      if (formData.role === 'driver') {
        if (!formData.assigned_unit_id) {
          toast.error('Please select a unit for the driver');
          return;
        }
        payload.assigned_unit_id = formData.assigned_unit_id;
        payload.permissions = formData.permissions;
      }
      
      await usersApi.update(editingUser.id, payload);
      toast.success('User updated successfully');
      setDialogOpen(false);
      setEditingUser(null);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'dispatcher',
      assigned_unit_id: '',
      permissions: {
        can_view_weekly_summary: true,
        can_view_driver_files: true,
        can_upload_files: true,
      },
    });
    setEditingUser(null);
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
      assigned_unit_id: user.assigned_unit_id || '',
      permissions: user.permissions || {
        can_view_weekly_summary: true,
        can_view_driver_files: true,
        can_upload_files: true,
      },
      feature_access: user.feature_access || {
        schedule: true, accounting: true, deductions: true,
        recurring_deductions: true, history: true, owner_operators: true,
        vehicle_management: true, driver_files: true,
      },
    });
    setDialogOpen(true);
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await usersApi.updateRole(userId, newRole);
      toast.success('Role updated successfully');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await usersApi.delete(userId);
      toast.success('User deleted successfully');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const roleColors = {
    admin: 'bg-violet-100 text-violet-700',
    manager: 'bg-indigo-100 text-indigo-700',
    dispatcher: 'bg-blue-100 text-blue-700',
    staff: 'bg-cyan-100 text-cyan-700',
    viewer: 'bg-slate-100 text-slate-700',
    driver: 'bg-green-100 text-green-700',
    owner_operator: 'bg-amber-100 text-amber-700',
  };

  const roleIcons = {
    admin: Shield,
    manager: Shield,
    dispatcher: UserCheck,
    staff: UserCheck,
    viewer: Eye,
    driver: Truck,
    owner_operator: Truck,
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="users-loading">
        <Skeleton className="h-8 w-48" />
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-64 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="users-page">
      {/* Header — hidden when embedded inside CompanySettings tab */}
      <div className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 ${embedded ? 'sm:justify-end' : ''}`}>
        {!embedded && (
          <div>
            <h1 className="text-3xl font-manrope font-bold text-foreground">User Management</h1>
            <p className="text-muted-foreground mt-1">Manage team members and their roles</p>
          </div>
        )}
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-user-btn">
              <UserPlus className="h-4 w-4 mr-2" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="font-manrope">
                {editingUser ? 'Edit User' : 'Add New User'}
              </DialogTitle>
              <DialogDescription>
                {editingUser 
                  ? 'Update user details and permissions.'
                  : 'Create a new user account and assign their role permissions.'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={editingUser ? handleUpdateUser : handleCreateUser} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Doe"
                  required
                  data-testid="user-name-input"
                />
              </div>
              
              {!editingUser && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="john@company.com"
                      required
                      data-testid="user-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Password *</Label>
                    <Input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="••••••••"
                      required
                      minLength={6}
                      data-testid="user-password-input"
                    />
                  </div>
                </>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="role">Role *</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">
                      <div className="flex items-center gap-2">
                        <Shield className="h-4 w-4 text-violet-600" />
                        <div>
                          <span className="font-medium">Admin</span>
                          <p className="text-xs text-muted-foreground">Full access to all features</p>
                        </div>
                      </div>
                    </SelectItem>
                    <SelectItem value="manager">
                      <div className="flex items-center gap-2">
                        <Shield className="h-4 w-4 text-indigo-600" />
                        <div>
                          <span className="font-medium">Manager</span>
                          <p className="text-xs text-muted-foreground">Full ops, no destructive financial deletes</p>
                        </div>
                      </div>
                    </SelectItem>
                    <SelectItem value="dispatcher">
                      <div className="flex items-center gap-2">
                        <UserCheck className="h-4 w-4 text-blue-600" />
                        <div>
                          <span className="font-medium">Dispatcher</span>
                          <p className="text-xs text-muted-foreground">Daily ops — loads, schedule, drivers</p>
                        </div>
                      </div>
                    </SelectItem>
                    <SelectItem value="staff">
                      <div className="flex items-center gap-2">
                        <UserCheck className="h-4 w-4 text-cyan-600" />
                        <div>
                          <span className="font-medium">Staff</span>
                          <p className="text-xs text-muted-foreground">Office staff — drivers, files, summaries</p>
                        </div>
                      </div>
                    </SelectItem>
                    <SelectItem value="viewer">
                      <div className="flex items-center gap-2">
                        <Eye className="h-4 w-4 text-slate-600" />
                        <div>
                          <span className="font-medium">Viewer</span>
                          <p className="text-xs text-muted-foreground">Read-only access</p>
                        </div>
                      </div>
                    </SelectItem>
                    <SelectItem value="driver">
                      <div className="flex items-center gap-2">
                        <Truck className="h-4 w-4 text-green-600" />
                        <div>
                          <span className="font-medium">Driver</span>
                          <p className="text-xs text-muted-foreground">View assigned unit only</p>
                        </div>
                      </div>
                    </SelectItem>
                    <SelectItem value="owner_operator">
                      <div className="flex items-center gap-2">
                        <Truck className="h-4 w-4 text-amber-600" />
                        <div>
                          <span className="font-medium">Owner-Operator</span>
                          <p className="text-xs text-muted-foreground">Contracted truck owner — own loads & settlements</p>
                        </div>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Driver-specific fields */}
              {formData.role === 'driver' && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="unit">Assigned Unit *</Label>
                    <Select
                      value={formData.assigned_unit_id}
                      onValueChange={(value) => setFormData({ ...formData, assigned_unit_id: value })}
                    >
                      <SelectTrigger data-testid="user-unit-select">
                        <SelectValue placeholder="Select unit" />
                      </SelectTrigger>
                      <SelectContent>
                        {units.map((unit) => (
                          <SelectItem key={unit.id} value={unit.id}>
                            <div className="flex items-center gap-2">
                              <Truck className="h-4 w-4 text-blue-600" />
                              <span>{unit.unit_number}</span>
                              {unit.make && <span className="text-muted-foreground">({unit.make} {unit.model})</span>}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
                    <Label className="text-sm font-medium">Driver Permissions</Label>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">View Weekly Summary</p>
                        <p className="text-xs text-muted-foreground">Can view their unit&apos;s weekly summary</p>
                      </div>
                      <Switch
                        checked={formData.permissions.can_view_weekly_summary}
                        onCheckedChange={(checked) => setFormData({
                          ...formData,
                          permissions: { ...formData.permissions, can_view_weekly_summary: checked }
                        })}
                        data-testid="perm-weekly-summary"
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">View Driver Files</p>
                        <p className="text-xs text-muted-foreground">Can view driver documents</p>
                      </div>
                      <Switch
                        checked={formData.permissions.can_view_driver_files}
                        onCheckedChange={(checked) => setFormData({
                          ...formData,
                          permissions: { ...formData.permissions, can_view_driver_files: checked }
                        })}
                        data-testid="perm-driver-files"
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">Upload Files</p>
                        <p className="text-xs text-muted-foreground">Can upload documents (cannot delete)</p>
                      </div>
                      <Switch
                        checked={formData.permissions.can_upload_files}
                        onCheckedChange={(checked) => setFormData({
                          ...formData,
                          permissions: { ...formData.permissions, can_upload_files: checked }
                        })}
                        data-testid="perm-upload-files"
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Feature Access — for non-driver users */}
              {formData.role !== 'driver' && (
                <div className="space-y-2 p-3 rounded-lg border">
                  <Label className="text-sm font-medium">Feature Access</Label>
                  <p className="text-xs text-muted-foreground">Control which pages this user can see</p>
                  {[
                    { key: 'schedule', label: 'Schedule' },
                    { key: 'accounting', label: 'Accounting' },
                    { key: 'deductions', label: 'Deductions' },
                    { key: 'recurring_deductions', label: 'Recurring Deductions' },
                    { key: 'history', label: 'History' },
                    { key: 'owner_operators', label: 'Owner Operators' },
                    { key: 'vehicle_management', label: 'Vehicle Management' },
                    { key: 'driver_files', label: 'Driver Files' },
                  ].map(f => (
                    <div key={f.key} className="flex items-center justify-between py-1">
                      <Label className="text-xs cursor-pointer">{f.label}</Label>
                      <Switch
                        checked={formData.feature_access?.[f.key] !== false}
                        onCheckedChange={(checked) => setFormData({
                          ...formData,
                          feature_access: { ...formData.feature_access, [f.key]: checked }
                        })}
                        data-testid={`user-access-${f.key}`}
                      />
                    </div>
                  ))}

                  {/* Dispatcher / Staff scoping toggle */}
                  {['dispatcher', 'staff'].includes(formData.role) && (
                    <div className="mt-3 pt-3 border-t">
                      <div className="flex items-center justify-between gap-3 p-2 rounded bg-amber-50/40 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800">
                        <div>
                          <Label className="text-xs font-semibold cursor-pointer">Limit to Assigned Units Only</Label>
                          <p className="text-[10px] text-muted-foreground">When ON, this user only sees units assigned to them (Loads, Schedule, Summary). When OFF, they see everything.</p>
                        </div>
                        <Switch
                          checked={!!formData.feature_access?.limit_to_assigned_units}
                          onCheckedChange={(checked) => setFormData({
                            ...formData,
                            feature_access: { ...formData.feature_access, limit_to_assigned_units: checked }
                          })}
                          data-testid="user-access-limit-units"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="user-submit-btn">
                  {editingUser ? 'Update User' : 'Create User'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Role Legend */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-manrope">Role Permissions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="flex items-start gap-3 p-3 bg-violet-50 rounded-lg">
              <Shield className="h-5 w-5 text-violet-600 mt-0.5" />
              <div>
                <p className="font-medium text-violet-700">Admin</p>
                <p className="text-sm text-violet-600">Full access. Can manage users, delete units/drivers, and all CRUD operations.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
              <UserCheck className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <p className="font-medium text-blue-700">Dispatcher</p>
                <p className="text-sm text-blue-600">Can create and edit units, drivers, loads, and deductions. Cannot delete or manage users.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
              <Eye className="h-5 w-5 text-slate-600 mt-0.5" />
              <div>
                <p className="font-medium text-slate-700">Viewer</p>
                <p className="text-sm text-slate-600">Read-only access. Can view all data and export reports.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
              <Truck className="h-5 w-5 text-green-600 mt-0.5" />
              <div>
                <p className="font-medium text-green-700">Driver</p>
                <p className="text-sm text-green-600">Restricted view for assigned unit only. Can upload files but cannot delete.</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Assigned Unit</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    <Users className="h-8 w-8 mx-auto mb-2 text-muted-foreground/50" />
                    <p>No users found</p>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => {
                  const RoleIcon = roleIcons[user.role] || UserCheck;
                  const isCurrentUser = user.id === currentUser?.id;
                  
                  return (
                    <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-10 w-10 bg-blue-600">
                            <AvatarFallback className="bg-blue-600 text-white">
                              {user.name?.charAt(0)?.toUpperCase() || 'U'}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{user.name}</p>
                            {isCurrentUser && (
                              <span className="text-xs text-blue-600">(You)</span>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{user.email}</TableCell>
                      <TableCell>
                        <Badge className={roleColors[user.role] || roleColors.dispatcher}>
                          <RoleIcon className="h-3 w-3 mr-1" />
                          {user.role}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {user.role === 'driver' && user.assigned_unit_number ? (
                          <Badge variant="outline" className="bg-green-50">
                            <Truck className="h-3 w-3 mr-1" />
                            {user.assigned_unit_number}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDate(user.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {!isCurrentUser && (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openEditDialog(user)}
                                className="text-blue-600 hover:text-blue-700"
                                data-testid={`edit-user-${user.id}`}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDelete(user.id)}
                                className="text-red-600 hover:text-red-700"
                                data-testid={`delete-user-${user.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
